import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentIcon from "@mui/icons-material/Payment";
import { getOrderInfo } from "../../services/UserApi/OrderApi";
import { getImageUrl } from "../../services/indexMediaBaseUrl";
import { useTranslation } from "react-i18next";

export default function OrderDialog({ open = false, onClose = () => {}, orderId = null }) {
  const { t, i18n } = useTranslation("order_dialog");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const lang = (() => {
    let l = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
    if (l === "rn") l = "en";
    return l.startsWith("ar") ? "ar" : "en";
  })();
  const isAr = lang === "ar";

  useEffect(() => {
    let mounted = true;
    if (!open || !orderId) {
      setOrder(null);
      setError("");
      setLoading(false);
      return () => { mounted = false; };
    }

    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getOrderInfo(orderId);
        const data = res?.data || res;
        if (!mounted) return;
        setOrder(data || null);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || err?.message || t("fetch_failed"));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [open, orderId, t]);

  const getProductName = (p) => {
    if (!p) return "";
    return isAr ? (p.name_ar || p.name_en || "") : (p.name_en || p.name_ar || "");
  };

  const resolveImage = (photo) => {
    if (!photo) return "/placeholder.png";
    try {
      return getImageUrl(photo);
    } catch {
      return photo;
    }
  };

  const getStatusColor = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "delivered" || s === "confirmed") return "success";
    if (s === "cancelled" || s === "rejected") return "error";
    if (s === "pending") return "warning";
    return "default";
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
    >
      {/* Luxury Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
          color: "white",
          p: 3,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReceiptIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>
                {t("order_details")}
              </Typography>
              {order && (
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                  #{order.order_number || order.order_id}
                </Typography>
              )}
            </Box>
          </Stack>

          <IconButton
            onClick={onClose}
            sx={{
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.2)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.3)",
                transform: "rotate(90deg)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
            <CircularProgress size={56} sx={{ color: "primary.main" }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            {error}
          </Alert>
        ) : !order ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography color="text.secondary" fontWeight={600}>
              {t("no_data")}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Order Summary Card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "grey.100",
                bgcolor: "primary.50",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t("status")}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={order.status}
                      color={getStatusColor(order.status)}
                      sx={{ fontWeight: 700, height: 32 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t("payment_method", { defaultValue: "طريقة الدفع" })}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      icon={<PaymentIcon />}
                      label={order.payment_method || "-"}
                      sx={{
                        bgcolor: "background.paper",
                        fontWeight: 700,
                        height: 32,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Financial Summary */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "grey.100",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: "primary.dark" }}>
                {t("financial_summary", { defaultValue: "الملخص المالي" })}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t("subtotal")}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {order.subtotal} {isAr ? "ج.م" : "EGP"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t("discount", { defaultValue: "الخصم" })}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    -{order.discount || 0} {isAr ? "ج.م" : "EGP"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t("shipping")}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {order.shipping} {isAr ? "ج.م" : "EGP"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {t("total")}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="primary.main">
                    {order.total} {isAr ? "ج.م" : "EGP"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Order Items */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "grey.100",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <ShoppingBagIcon sx={{ color: "primary.main", fontSize: 24 }} />
                <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
                  {t("order_items", { defaultValue: "عناصر الطلب" })} ({order.items?.length || 0})
                </Typography>
              </Stack>

              {Array.isArray(order.items) && order.items.length > 0 ? (
                <Stack spacing={2}>
                  {order.items.map((it, idx) => {
                    const prod = it.product || {};
                    const photo = prod.main_photo || prod.images?.[0]?.image || "";
                    const productName = getProductName(prod) || `${t("product")} ${it.product_id}`;

                    return (
                      <Paper
                        key={it.product_id || it.id || idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "grey.100",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: "background.paper",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Avatar
                                variant="rounded"
                                src={resolveImage(photo)}
                                sx={{
                                  width: 64,
                                  height: 64,
                                  border: "2px solid",
                                  borderColor: "grey.200",
                                }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {productName}
                                </Typography>
                                {it.size && (
                                  <Chip
                                    label={`${t("size")}: ${it.size?.size || it.size?.name || it.size}`}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      height: 20,
                                      fontSize: "0.7rem",
                                      bgcolor: "primary.50",
                                      color: "primary.dark",
                                    }}
                                  />
                                )}
                                {it.color && (
                                  <Chip
                                    label={`${t("color", { defaultValue: "اللون" })}: ${it.color?.color_en || it.color?.color_ar || it.color}`}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      ml: 0.5,
                                      height: 20,
                                      fontSize: "0.7rem",
                                      bgcolor: "grey.100",
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={4} sm={2}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {t("quantity")}
                            </Typography>
                            <Typography variant="body1" fontWeight={700}>
                              {it.quantity}
                            </Typography>
                          </Grid>

                          <Grid item xs={4} sm={2}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {t("price")}
                            </Typography>
                            <Typography variant="body1" fontWeight={700}>
                              {it.price}
                            </Typography>
                          </Grid>

                          <Grid item xs={4} sm={2}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {t("total")}
                            </Typography>
                            <Typography variant="body1" fontWeight={800} color="primary.main">
                              {it.total}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    {t("no_items")}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Shipping Information */}
            {order.checkout && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "grey.100",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <LocalShippingIcon sx={{ color: "primary.main", fontSize: 24 }} />
                  <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
                    {t("shipping_info", { defaultValue: "معلومات الشحن" })}
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t("phone", { defaultValue: "الهاتف" })}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {order.checkout.phone || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t("address", { defaultValue: "العنوان" })}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {order.checkout.address || "-"}
                    </Typography>
                  </Grid>
                  {order.checkout.notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t("notes", { defaultValue: "ملاحظات" })}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ fontStyle: "italic" }}>
                        {order.checkout.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: "grey.50", borderTop: "1px solid", borderColor: "grey.100" }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            px: 4,
            py: 1.2,
            fontWeight: 700,
            borderRadius: 2,
            textTransform: "none",
            background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
            },
          }}
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
